<script setup lang="ts">
import axios from 'axios'

const { t } = useI18n()
const { isDirection } = useAccess()
const { user, userHasClients, resetUseStorageItems, stores, languages } = useLocalStorage()
const router = useRouter()

/*
 * Compatibility mode for userHasClients for "My Subscriptions" menu.
 * This has been added to allow already authenticated users to see the menu without login again.
 * Can be removed on 2025-03-20 (arbitrarily chosen date, 1 month from now)
 */
if (userHasClients.value === null) {
  axios.get(`clients`).then((response) => {
    userHasClients.value = response.data.data.length > 0
  })
}

const defaultLanguage = ref('fr')

const countStores = computed(() => {
  return stores.value?.length
})

const currentLanguage = computed(() => {
  const userLocale = user.value?.language?.code || defaultLanguage.value
  const result = languages.value.find(({ code: langCode }) => langCode === userLocale) || {}
  return result.iso639_1
})

const sortedLanguages = computed(() => {
  return sortLanguages()
})

const storeSettingTo = () => {
  if (countStores.value === 1) {
    return { name: 'StoreEdit', params: { storeId: stores.value[0].id } }
  } else {
    return { name: 'StoreList' }
  }
}

const logout = () => {
  resetUseStorageItems()
  router.push({
    name: 'Login'
  })
}

const sortLanguages = () => {
  return [...languages.value].sort((a) => {
    if (a.iso639_1 === defaultLanguage.value) {
      return -1
    }
    return 1
  })
}

const updateUserLanguage = async (locale: string) => {
  if (currentLanguage.value === locale) {
    return
  }

  router.push({ name: 'Autologin', query: { lang: locale } })
}

const itemLinkClasses = 'group flex cursor-pointer items-center gap-4 px-4 py-2 text-sm text-foreground outline-hidden'
const itemResetClasses = 'p-0 rounded-none'
</script>

<template>
  <div class="divide-y divide-border" role="menu" aria-orientation="vertical">
    <div class="py-1" role="none">
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="{ name: 'VisualIdentityEdit' }" :class="itemLinkClasses">
          <BaseIcon name="HeroSwatchIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.visualIdentity') }}
        </router-link>
      </DropdownMenuItem>
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="storeSettingTo()" :class="itemLinkClasses">
          <BaseIcon name="HeroBuildingOfficeIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.store', countStores) }}
        </router-link>
      </DropdownMenuItem>
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="{ name: 'UserList' }" :class="itemLinkClasses">
          <BaseIcon name="HeroUserGroupIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.users') }}
        </router-link>
      </DropdownMenuItem>
      <DropdownMenuItem v-if="userHasClients" as-child :class="itemResetClasses">
        <router-link id="invoice-list" :to="{ name: 'InvoiceList' }" :class="itemLinkClasses">
          <BaseIcon name="HeroCalculatorIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.billing') }}
        </router-link>
      </DropdownMenuItem>
    </div>
    <div v-if="isDirection" class="py-1" role="none">
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="{ name: 'DomainSettings' }" :class="itemLinkClasses">
          <BaseIcon name="IconoirSendMailOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.domain') }}
        </router-link>
      </DropdownMenuItem>
    </div>
    <div class="py-1" role="none">
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="{ name: 'ExportList' }" :class="itemLinkClasses">
          <BaseIcon name="HeroArrowDownCircleIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.exports') }}
        </router-link>
      </DropdownMenuItem>
      <DropdownMenuItem as-child :class="itemResetClasses">
        <router-link :to="{ name: 'ImportList' }" :class="itemLinkClasses">
          <BaseIcon name="HeroArrowUpCircleIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('sidebar.settings.imports') }}
        </router-link>
      </DropdownMenuItem>
    </div>
    <div class="py-1" role="none">
      <DropdownMenuItem as-child :class="itemResetClasses">
        <a href="https://heypongohelp.zendesk.com/hc/fr" target="_blank" :class="itemLinkClasses">
          <BaseIcon name="HeroQuestionMarkCircleIconOutline" class="h-5 w-5 text-foreground-secondary" />
          <span class="flex-1">{{ t('sidebar.settings.help') }}</span>
          <BaseIcon name="HeroArrowTopRightOnSquareIconSolid" class="h-4 w-4 text-muted-foreground" />
        </a>
      </DropdownMenuItem>
    </div>
    <div v-if="!!languages" class="language-menu-button" role="none">
      <div
        class="group relative flex cursor-pointer items-center gap-4 px-4 py-2 text-sm text-foreground transition-colors duration-200 ease-out hover:bg-accent"
        role="menuitem"
      >
        <BaseIcon name="HeroFlagIconOutline" class="h-5 w-5 text-foreground-secondary" />
        <span class="flex-1">{{ t('sidebar.settings.language') }}</span>
        <BaseIcon name="HeroChevronRightIconSolid" class="size-4 shrink-0 text-muted-foreground" />
      </div>

      <div class="language-menu">
        <ul>
          <DropdownMenuItem v-for="language in sortedLanguages" :key="language.code" as-child :class="itemResetClasses">
            <li
              class="m-1 flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent focus:bg-accent outline-hidden"
              :class="
                currentLanguage === language.iso639_1 ? 'text-muted-foreground pointer-events-none' : 'cursor-pointer'
              "
              @click="updateUserLanguage(language.iso639_1)"
            >
              <img class="h-6 w-6 rounded-full object-cover" :src="language.flag" :title="language.nativeName" />
              {{ language.nativeName }}
              ({{ language.code }})
            </li>
          </DropdownMenuItem>
        </ul>
      </div>
    </div>
    <div class="py-1" role="none">
      <DropdownMenuItem as-child :class="itemResetClasses">
        <button type="button" :class="[itemLinkClasses, 'w-full']" @click="logout">
          <BaseIcon name="HeroLockOpenIconOutline" class="h-5 w-5 text-foreground-secondary" />
          {{ t('global.words.logout') }}
        </button>
      </DropdownMenuItem>
    </div>
    <!-- Mobile lang switch -->
    <div class="py-1 md:hidden">
      <ul class="flex">
        <DropdownMenuItem v-for="language in sortedLanguages" :key="language.code" as-child :class="itemResetClasses">
          <li
            class="cursor-pointer rounded-md px-5 py-3 hover:bg-accent focus:bg-accent outline-hidden"
            :class="
              currentLanguage === language.iso639_1 ? 'text-muted-foreground pointer-events-none' : 'cursor-pointer'
            "
            @click="updateUserLanguage(language.iso639_1)"
          >
            <div class="flex items-center gap-2 text-sm">
              <img class="h-6 w-6 rounded-full object-cover" :src="language.flag" :title="language.nativeName" />
              ({{ language.code }})
            </div>
          </li>
        </DropdownMenuItem>
      </ul>
    </div>
  </div>
</template>

<style>
@reference "@/input.css";

.language-menu-button {
  @apply hidden py-1 md:block;
}
.language-menu {
  @apply absolute -bottom-2 left-full min-w-[180px] rounded-xl border border-border bg-white p-1 shadow-md transition-opacity opacity-0 pointer-events-none;
}
.language-menu-button:hover .language-menu,
.language-menu:hover {
  @apply opacity-100 pointer-events-auto;
}
</style>
